import { Formik, FormikValues } from 'formik';
import React, { useContext } from 'react';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { Components } from './styled';
import ProjectTextField from '../../inputs/ProjectTextField';
import { ToastContext } from '../../../providers/ToastProvider/context';
import useModalState from '../../../hooks/useModalState';
import { isProjectError } from '../../../utils/errors';
import ProjectModal from '../../ProjectModal';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { Campaign } from '../../../api/types/campaign';
import { createCampaign, updateCampaign } from '../../../api/campaign';
import { CreateCampaignDto } from '../../../api/typeDtos/campaign';
import { MinimalCampaignSchema } from '../../../api/campaign/parser';
import { ReactComponent as AccessLinkIcon } from '../../../assets/icons/icon-grey-check-square-offset.svg';

const initialValues = {
  name: '',
  branchUrlAlias: '',
  offering: '',

  isDisabled: false,

  branchUrl: '',
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
}: {
  modalState?: Campaign & { branchUrlAlias?: string };
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const campaignInitialValues = {
    name: modalState?.name,
    branchUrlAlias: modalState?.branchUrlAlias,
    offering: modalState?.offering,

    isDisabled: !modalState?.isDisabled,

    branchUrl: modalState?.branchUrl,
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
    });
  };

  return (
    <Formik
      initialValues={modalState ? campaignInitialValues : initialValues}
      validationSchema={MinimalCampaignSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Name</Components.FieldLabel>
                  <ProjectTextField
                    value={values?.name}
                    onChangeText={handleChange}
                    name={'name'}
                    errorMessage={errors.name && touched.name ? errors?.name : undefined}
                    placeholder={'Enter campaign name (same as $marketing_title)'}
                  />
                </Components.FieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldsRowWrapper>
                  {!modalState?.branchUrl && (
                    <Components.FieldContainer widthPercent={30}>
                      <Components.FieldLabel>Branch URL Alias (Optional)</Components.FieldLabel>
                      <ProjectTextField
                        value={values?.branchUrlAlias || values?.branchUrlAlias ? values?.branchUrlAlias : ''}
                        onChangeText={handleChange}
                        name={'branchUrlAlias'}
                        errorMessage={
                          errors.branchUrlAlias && touched.branchUrlAlias ? errors?.branchUrlAlias : undefined
                        }
                        placeholder={'Enter branch URL alias'}
                        disabled={!!modalState?.branchUrl}
                      />
                      <Components.Description>
                        {
                          'This unique text will be used to create the branch URL (e.g.: https://uu58n.app.link/<branch url alias>)'
                        }
                      </Components.Description>
                    </Components.FieldContainer>
                  )}
                  {!!modalState?.branchUrl ? (
                    <Components.FieldContainer widthPercent={70}>
                      <Components.FieldLabel>Branch URL (Read Only)</Components.FieldLabel>
                      <Components.BranchURLWrapper>
                        <ProjectTextField
                          value={
                            modalState?.branchUrl
                              ? modalState?.branchUrl
                              : values?.branchUrl || values?.branchUrl
                              ? values?.branchUrl
                              : ''
                          }
                          onChangeText={handleChange}
                          name={'branchUrl'}
                          errorMessage={errors.branchUrl && touched.branchUrl ? errors?.branchUrl : undefined}
                          placeholder={'Branch URL'}
                          disabled={true}
                        />
                        <Components.IconWrapper onClick={() => window.open(modalState?.branchUrl, '_blank')}>
                          <AccessLinkIcon />
                        </Components.IconWrapper>
                      </Components.BranchURLWrapper>
                    </Components.FieldContainer>
                  ) : null}
                </Components.FieldsRowWrapper>
                <Components.FieldLabel>Campaign offering</Components.FieldLabel>
                <ProjectTextField
                  value={values?.offering || values?.offering ? values?.offering : ''}
                  onChangeText={handleChange}
                  name={'offering'}
                  errorMessage={errors.offering && touched.offering ? errors?.offering : undefined}
                  placeholder={'Enter RevenueCat offering identifier'}
                />
                <Components.Description>
                  {
                    'When the user taps the branch url, the app will open. If this field is filled, the user will also see the chosen paywall.'
                  }
                </Components.Description>
                <Components.CheckboxFieldWrapper>
                  <Components.Checkbox checked={values?.isDisabled} onChange={handleChange} name={'isDisabled'} />
                  <Components.CheckboxLabel>Is Active</Components.CheckboxLabel>
                </Components.CheckboxFieldWrapper>
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditCampaignModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  setModalState,
  refreshCallback,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { campaign?: Campaign };
  setModalState?: (state: { campaign?: Campaign }) => void;
  refreshCallback?: () => Promise<void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast } = useContext(ToastContext);

  const confirmationModal = useModalState<boolean>();

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _onSave = async (values: FormikValues) => {
    const data: CreateCampaignDto = {
      name: values?.name,
      type: 'campaign',
      branchUrlAlias: values?.branchUrlAlias,
      offering: values?.offering,

      isDisabled: !values?.isDisabled,
      ...(modalState?.campaign?.branchUrl ? { branchUrl: modalState?.campaign?.branchUrl } : {}),
    };

    if (modalState?.campaign?.id) {
      delete data.branchUrlAlias;
    }

    setIsLoading(true);
    const result =
      modalState && modalState?.campaign?.id
        ? await updateCampaign({ ...data, id: modalState?.campaign?.id })
        : await createCampaign(data);
    setIsLoading(false);

    const resultError = isProjectError(result);
    if (resultError) {
      showErrorToast(resultError);
    } else {
      modalState && modalState?.campaign?.id
        ? showSuccessToast('The changes were saved successfully')
        : showSuccessToast('You successfully added a campaign');

      if (!modalState?.campaign?.branchUrl) {
        if (!isProjectError(result) && setModalState && result) {
          // @ts-ignore
          setModalState({ campaign: result });
        }
      }
    }

    refreshCallback?.().then();
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={<ModalBody modalState={modalState?.campaign} onSave={_onSave} onCancel={_onModalClose} />}
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.campaign ? `Edit campaign` : 'Create new campaign'}
        titleSuffix={modalState?.campaign ? `(ID: ${modalState?.campaign?.id})` : undefined}
        disableCloseOnClickAway
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.lessons.cancelEditing}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};

export default CreateOrEditCampaignModal;
